import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './ProjectCaseStudy.module.css';

const IMG_BASE = "/assets/project/Sky Sentinel\u00a0S26 Next‑Generation Air‑Defense Interceptor";

const Figure = ({ src, alt, caption }) => (
  <figure className={styles.figureCard}>
    <img src={src} alt={alt} className={styles.galleryImage} />
    <figcaption className={styles.galleryCaption}>
      {caption}
    </figcaption>
  </figure>
);

export default function SkySentinelPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className={styles.articleContainer}>

      <button className={styles.backButton} onClick={() => navigate('/projects')}>
        ← Back to Projects
      </button>

      <header className={styles.header}>
        <h1 className={styles.title}>
          Sky Sentinel S26: Next-Generation Air Defense Interceptor
        </h1>
        <div className={styles.authors}>
          <span className={styles.highlightAuthor}>Jolok Banarjee (Individual Aircraft Design Capstone) • Feb 2025 – Jul 2025</span>
        </div>
        <div className={styles.affiliations}>
          Supervised by: Air Cdre Air Cdre Dr. Saifur Rahman Bakaul (Chairman, Dept. of Aerospace Engineering, AAUB) &
          Lecturer Md. Toufiq Islam Noor
        </div>
        <div className={styles.badge} style={{ background: 'rgba(250, 204, 21, 0.12)', color: '#d97706', border: '1px solid rgba(217, 119, 6, 0.3)' }}>
          🏆 Best Aerospace Vehicle Design 2025 — AAUB
        </div>
        <div className={styles.headerActions}>
          <a
            href="https://shorturl.at/iWWk5"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.85rem',
              fontWeight: 700,
              color: '#d97706',
              textDecoration: 'none',
              background: 'rgba(217, 119, 6, 0.06)',
              padding: '0.5rem 1.05rem',
              borderRadius: '8px',
              border: '1px solid rgba(217, 119, 6, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem'
            }}
          >
            📄 View Design Report →
          </a>
        </div>
      </header>

      <div className={styles.abstractBox}>
        <h3 className={styles.abstractTitle}>Project Overview</h3>
        <p className={styles.abstractText}>
          The Sky Sentinel S26 is a conceptual next-generation air-defense interceptor engineered as my final-year academic project to fulfill advanced air superiority and rapid interception missions. As the Aerodynamics & Design Lead, I spearheaded the iterative conceptual design, aerodynamic stability analysis, mission profile optimization, and overall performance sizing of the aircraft. The project culminated in a highly capable supersonic platform optimized for rapid deployment, short-field performance, and high-altitude maneuverability. The design was awarded the <strong>Best Aerospace Vehicle Design 2025</strong> by the Department of Aerospace Engineering, AAUB.
        </p>
        <div className={styles.keywords}>
          <strong>Focus Areas:</strong> Aircraft Design, Aerodynamics, Performance Sizing, MATLAB, Supersonic Flight, Mission Profile Analysis, Weight Estimation.
        </div>
      </div>

      <section>
        <h2 className={styles.sectionTitle}>Design Report & Mission Profile</h2>
        <p className={styles.paragraph}>
          The project began with a comprehensive design report defining the operational requirements, mission phases, and performance constraints for a modern air-defense interceptor. The S26 was designed around an 11-phase mission profile encompassing warmup, take-off, climb, cruise, 2-hour patrol, intercept dash, 360° combat turn, cruise back, loiter, descent, and landing — yielding a 750 nautical mile combat radius.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/profile_page.png`}
            alt="Design Report Cover"
            caption="Figure 1: Sky Sentinel-S26 Design Report cover page — Final Year Academic Project, June 2025."
          />
          <Figure
            src={`${IMG_BASE}/mission profile.png`}
            alt="Mission Profile Diagram"
            caption="Figure 2: Complete mission profile diagram showing all 11 flight phases from take-off to landing."
          />
        </div>
        <div className={styles.galleryGrid} style={{ marginTop: '1.25rem' }}>
          <Figure
            src={`${IMG_BASE}/mission profile phases.png`}
            alt="Mission Profile Phases Table"
            caption="Figure 3: Mission phase breakdown with time, distance, and operational parameters for each segment."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Aerodynamic Sizing & Weight Estimation</h2>
        <p className={styles.paragraph}>
          Designing an interceptor capable of sustained supersonic flight requires meticulous balancing of wave drag, thrust-to-weight ratios, and transonic stability. I utilized <strong>MATLAB</strong> to develop iterative performance sizing models, constraint analysis, and computational weight estimation. The constraint graph identifies the feasible design region by overlaying take-off, climb, cruise, sustained turn, stall speed, landing distance, and thrust-matching requirements.
        </p>
        <p className={styles.paragraph}>
          Payload trade studies were conducted across 10 payload configurations (1,000–12,000 lb) to determine the optimal gross weight. The refined weight analysis converged on a 2,000 lb payload yielding a take-off gross weight of 44,427.68 lb, with corresponding fuel and crew weight allocations.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/matlab code.png`}
            alt="MATLAB Weight Estimation Code"
            caption="Figure 4: MATLAB script for iterative take-off gross weight (W₀) convergence using the guess-and-calculate method."
          />
          <Figure
            src={`${IMG_BASE}/constrain graph.png`}
            alt="Constraint Analysis Graph"
            caption="Figure 5: Constraint analysis graph plotting thrust-to-weight vs. wing loading with feasible design region highlighted."
          />
        </div>
        <div className={styles.galleryGrid} style={{ marginTop: '1.25rem' }}>
          <Figure
            src={`${IMG_BASE}/payload trade.png`}
            alt="Payload Trade Study"
            caption="Figure 6: Payload trade study showing W₀ variation across 1,000–12,000 lb payload configurations."
          />
          <Figure
            src={`${IMG_BASE}/payload trade of refine weight.png`}
            alt="Refined Weight Analysis"
            caption="Figure 7: Refined weight breakdown for the selected 2,000 lb payload — fuel, crew, and refine weight allocation."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>3D Conceptual Design</h2>
        <p className={styles.paragraph}>
          The final configuration employs a highly efficient low-bypass afterburning turbofan, delta-wing planform with twin vertical stabilizers, and streamlined fuselage optimized for minimal supersonic wave drag. The 3D CAD model below illustrates the aerodynamic planform and structural layout developed during the conceptual design phase.
        </p>
        <div className={styles.galleryGrid}>
          <Figure
            src={`${IMG_BASE}/1.png`}
            alt="S26 Isometric Render"
            caption="Figure 8: Isometric 3D render of the Sky Sentinel S26 showing delta-wing configuration and twin-tail layout."
          />
          <Figure
            src={`${IMG_BASE}/2.png`}
            alt="S26 Top View Render"
            caption="Figure 9: Top-down 3D render illustrating wing planform, fuselage taper, and stabilizer positioning."
          />
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Tactical Performance Metrics</h2>
        <p className={styles.paragraph}>
          The S26 was designed with extreme kinematic performance as its primary objective. The theoretical performance metrics calculated during the detailed design phase establish it as a highly lethal interceptor platform.
        </p>

        <div className={styles.metricsGrid}>
          <div className={styles.metricCard}>
            <div className={styles.metricValue}>Mach 2.2</div>
            <div className={styles.metricLabel}>Maximum Dash Speed</div>
            <p className={styles.metricSubtext}>
              Enables rapid target interception at high altitudes.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>254 m/s</div>
            <div className={styles.metricLabel}>Initial Climb Rate</div>
            <p className={styles.metricSubtext}>
              Exceptional vertical acceleration for scramble missions.
            </p>
          </div>

          <div className={styles.metricCard}>
            <div className={styles.metricValue}>22° / sec</div>
            <div className={styles.metricLabel}>Maximum Turn Rate</div>
            <p className={styles.metricSubtext}>
              Provides a distinct advantage in close-quarters dogfighting.
            </p>
          </div>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Award & Recognition</h2>
        <p className={styles.paragraph}>
          This project was submitted as my final-year academic requirement for the B.Sc. in Aerospace Engineering at AAUB. Due to the high fidelity of the engineering process, comprehensive MATLAB-based analysis, and the operational viability of the design, the Sky Sentinel S26 was awarded the <strong>Certificate of Merit for Best Aerospace Vehicle Design — 2025</strong> by the Department of Aerospace Engineering, Aviation and Aerospace University, Bangladesh.
        </p>
        <div className={styles.galleryGrid} style={{ marginTop: '1.5rem' }}>
          <Figure
            src={`${IMG_BASE}/Best Aerospace Vechicle Design-2025.jpeg`}
            alt="Best Aerospace Vehicle Design 2025 Certificate"
            caption="Figure 10: Certificate of Merit awarded by AAUB Department of Aerospace Engineering for Best Aerospace Vehicle Design 2025."
          />
        </div>
        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <a
            href="https://shorturl.at/iWWk5"
            target="_blank"
            rel="noreferrer"
            style={{
              fontSize: '0.9rem',
              fontWeight: 700,
              color: '#d97706',
              textDecoration: 'none',
              background: 'rgba(217, 119, 6, 0.08)',
              padding: '0.55rem 1.25rem',
              borderRadius: '8px',
              border: '1px solid rgba(217, 119, 6, 0.25)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem'
            }}
          >
            📄 View Full Design Report →
          </a>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Conclusion</h2>
        <p className={styles.paragraph}>
          The Sky Sentinel S26 demonstrates the complete aircraft design workflow from mission specification and constraint analysis to 3D conceptual modeling and performance validation. By integrating MATLAB-based weight estimation, payload trade studies, and aerodynamic constraint analysis, the design achieves a balanced supersonic interceptor capable of Mach 2.2 dash, 254 m/s climb rate, and 22°/sec turn performance. To extend mission endurance beyond its base combat radius, the S26 is fully engineered with air-to-air refueling capabilities. This project not only fulfilled my final-year academic requirements but also earned institutional recognition as the Best Aerospace Vehicle Design of 2025, reflecting the rigor and practical applicability of the engineering methodology applied.
        </p>
      </section>

    </div>
  );
}